import {
  signInWithEmailAndPassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  // updateEmail,
  verifyBeforeUpdateEmail,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  getAuth,
  sendPasswordResetEmail
} from "firebase/auth";
import {
  getDoc,
  doc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { firebaseAuth, firestoreDb, storage } from "helpers/firebase";
import { Auth } from "store/types";
import { get, put, post } from "api";
import { sendEmail } from "helpers/emailService";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import * as Sentry from "@sentry/react";

export const login =
  ({ email, password }) =>
    async (dispatch) => {
      const data = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password
      );

      if (!data.user.emailVerified) {
        const auth = getAuth()
        await sendEmailVerification(auth.currentUser);
        await firebaseAuth.signOut()
        const error = new Error('Email not verified')
        error.code = 'auth/email-not-verified'
        throw error
      }
      await getUserInfo({
        uid: data.user.uid,
        emailVerifed: data.user.emailVerified
      })(dispatch);
    };

export const getUserInfo =
  ({ uid, emailVerified = false }) =>
    async (dispatch) => {
      try {
        const currentUserOptions = getAuth().currentUser;
        const docRef = doc(firestoreDb, "users", uid);
        const updateData = {
          emailVerified
        };
        if (currentUserOptions?.metadata?.lastSignInTime) {
          updateData.lastLogin = new Date(currentUserOptions.metadata.lastSignInTime).getTime();
        }
        await updateDoc(docRef, updateData)
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
          throw new Error("User does not exist.");
        }

        const user = docSnap.data();
        // get user role
        const roleRef = collection(firestoreDb, "roles");
        const roleQuery = query(
          roleRef,
          where("tenant", "==", user.tenant)
        );
        const roleSnap = await getDocs(roleQuery);
        let roleDict = {}
        let roles = roleSnap.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        roles.forEach(role => roleDict[role.roleKey] = role)

        if (!roleDict[user.role]) {
          // redirect to Error 500 page, user does not have verifiable role
          throw new Error("User role does not exist. " + user.role);
        }

        const currentUser = getAuth().currentUser;
        if (currentUser && currentUser.email && currentUser.email !== user.email && currentUser.emailVerified) {
          try {
            await updateDoc(docRef, { email: currentUser.email });
            const roleData = roleDict[user.role];
            if (roleData && roleData.userType) {
              await put({
                subUrl: `/${roleData.userType}/detail/${uid}`,
                data: {
                  [`${roleData.userType}email`]: currentUser.email,
                },
              });
            }
            user.email = currentUser.email;
          } catch (syncError) {
            console.error("Failed to sync updated email to backend:", syncError);
          }
        }

        dispatch({
          type: Auth.SET_USER_ROLES,
          payload: roleDict
        })

        const userPayload = {
          ...user,
          uid,
          emailVerified,
          role: roleDict[user.role],
        };
        // Tag all future Sentry events (errors, logs) with this user
        Sentry.setUser({
          id: uid,
          email: user.email || undefined,
          username: [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email || uid,
        });
        Sentry.setTag("user.role", user.role || "unknown");
        return dispatch({
          type: Auth.SET_USER,
          payload: { user: userPayload },
        });
      } catch (error) {
        ;
      }
    };

export const logout = () => async (dispatch) => {
  await firebaseAuth.signOut();
  Sentry.setUser(null);
  Sentry.setTag("user.role", "");
  dispatch({
    type: Auth.LOGOUT,
  });
};

export const getTenant = () => async (dispatch, getState) => {
  const { hostname } = window.location;
  const tenantRef = collection(firestoreDb, "tenants");
  const tenantQuery = query(
    tenantRef,
    where("domainWhitelist", "array-contains", hostname)
  );
  const result = await getDocs(tenantQuery);
  let tenant = result.docs[0];
  if (!tenant) {
    const error = new Error("Tenant does not exist.");
    error.code = "tenant/not-found";
    // todo catch the error and navigate user to 404 page
    throw error;
  }
  tenant = tenant.data();
  // Dispatch ID first
  if (result.docs[0] && result.docs[0].id) {
    dispatch({
      type: Auth.SET_TENANT_ID,
      payload: result.docs[0].id,
    });
  }
  await getSiteSettings(tenant.tenantSlug)(dispatch)
  dispatch({
    type: Auth.SET_TENANT,
    payload: tenant.tenantSlug,
  });
};

export const getSiteSettings = (tenant) => async (dispatch) => {
  const siteSettingRef = doc(firestoreDb, "site-settings", tenant);
  const siteSettingSnap = await getDoc(siteSettingRef);

  const locationRef = collection(firestoreDb, "site-settings", tenant, "locations")
  const locationQuery = query(locationRef)
  const locations = (await getDocs(locationQuery)).docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  let notificationSettings = {};
  try {
    const notificationConfigRef = doc(firestoreDb, "site-settings", tenant, "notifications", "config");
    const notificationConfigSnap = await getDoc(notificationConfigRef);
    notificationSettings = notificationConfigSnap.exists() ? notificationConfigSnap.data() : {};
  } catch (error) {
    console.warn("Failed to fetch notification settings (likely permission issue):", error.message);
  }

  dispatch({
    type: Auth.SET_SITE_SETTINGS,
    payload: {
      ...siteSettingSnap.data(),
      ...notificationSettings, // Merge notification settings (e.g. newsfeed: false)
      locations
    },
  });
}

export const getMemberProfile = (uid) => async (dispatch) => {
  const { data } = await get({
    subUrl: `/member/detail/${uid}`,
  });
  dispatch({
    type: Auth.SET_USER_FULL_DATA,
    payload: data,
  });
  return data;
};

export const getParentProfile = (uid) => async (dispatch) => {
  const { data } = await get({
    subUrl: `/parent/detail/${uid}`,
  });
  dispatch({
    type: Auth.SET_USER_FULL_DATA,
    payload: data,
  });
  return data;
};

export const getMenteeProfile = (uid) => async (dispatch) => {
  const { data } = await get({
    subUrl: `/mentee/detail/${uid}`,
  });
  dispatch({
    type: Auth.SET_USER_FULL_DATA,
    payload: data,
  });
  return data;
};

export const saveMemberProfile =
  ({ uid, data }) =>
    async (dispatch) => {
      const docRef = doc(firestoreDb, "users", uid);
      await updateDoc(docRef, {
        firstName: data.memberfname,
        lastName: data.memberlname,
        location: data.mentoringlocation,
      });
      await put({
        subUrl: `/member/detail/${uid}`,
        data,
      });
      return;
    };

export const saveParentProfile =
  ({ uid, data }) =>
    async (dispatch) => {
      const docRef = doc(firestoreDb, "users", uid);
      await updateDoc(docRef, {
        firstName: data.parentfname,
        lastName: data.parentlname,
        location: data.mentoringlocation,
      });
      await put({
        subUrl: `/parent/detail/${uid}`,
        data,
      });
      return;
    };

export const saveMenteeProfile =
  ({ uid, data }) =>
    async (dispatch) => {
      const docRef = doc(firestoreDb, "users", uid);
      await updateDoc(docRef, {
        firstName: data.menteefname,
        lastName: data.menteelname,
        location: data.menteelocation,
      });
      await put({
        subUrl: `/mentee/detail/${uid}`,
        data,
      });
      return;
    };

export const changePassword =
  ({ email, oldPassword, newPassword }) =>
    async (dispatch) => {
      const user = firebaseAuth.currentUser;
      const cred = EmailAuthProvider.credential(email, oldPassword);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, newPassword);
      const newCred = EmailAuthProvider.credential(email, newPassword);
      await reauthenticateWithCredential(user, newCred);
    };

export const updateUserEmail = ({ newEmail, userType }) => async (dispatch, getState) => {
  const currentUser = firebaseAuth.currentUser;
  await verifyBeforeUpdateEmail(currentUser, newEmail);
  return true;
}

export const regenerateQR = () => async (dispatch, getState) => {
  const { auth: { user } } = getState();
  try {
    const { data } = await post({
      subUrl: '/user/regenerate-qr',
      data: { uid: user.uid }
    });

    if (data.success) {
      dispatch({
        type: Auth.SET_USER,
        payload: {
          user: {
            ...user,
            qrCodeUrl: data.qrCodeUrl,
            qrCodeCount: (user.qrCodeCount || 0) + 1
          }
        }
      });
      return data;
    }
  } catch (error) {
    console.error("Error regenerating QR code:", error);
    throw error;
  }
};

const defaultRoleTypes = {
  'member': 'member',
  'guest': 'member',
  'parent': 'parent',
  'emerging-100': 'member',
  'collegiate-100': 'member'
}

export const signup = (data) => async (dispatch, getState) => {
  const { auth: { tenant, siteSettings } } = getState()
  const userInfo = {
    ...data,
    tenant,
    approveduser: false
  }
  delete userInfo.password
  delete userInfo.retypePassword

  const createdUser = await createUserWithEmailAndPassword(
    firebaseAuth,
    data.email,
    data.password
  );
  userInfo.createdDate = (new Date(createdUser.user.metadata.creationTime)).getTime()
  userInfo.lastLogin = (new Date(createdUser.user.metadata.lastSignInTime)).getTime()

  const docRef = doc(firestoreDb, "users", createdUser.user.uid)

  await setDoc(docRef, userInfo)
  const idToken = await createdUser.user.getIdToken()
  const { firstName, lastName, email, role, location } = userInfo
  await post({
    subUrl: `/${defaultRoleTypes[userInfo.role]}`,
    data: {
      firstName,
      lastName,
      role,
      location,
      email,
      uid: createdUser.user.uid,
    },
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });
  // Send welcome email
  const isMember = defaultRoleTypes[userInfo.role] === "member";
  const logo = siteSettings.emailLogo || `${window.location.origin}/img/logo.png`;
  const mentoringLocationName = siteSettings.locations.find((loc) => loc.id === location)?.name;

  // Best-effort: email sending errors should not block account creation.
  // Signup success should still allow the user to proceed to their onboarding flow.
  try {
    await sendEmail(
      isMember ? 'member-welcome' : 'parent-welcome',
      email,
      {
        fullName: `${firstName} ${lastName}`,
        name: `${firstName} ${lastName}`,
        siteName: siteSettings.siteName,
        mentoringLocation: mentoringLocationName,
        signInLink: `${window.location.origin}/login`,
        contactEmail: siteSettings.contactEmail
      },
      {
        logo,
        headers: {
          Authorization: `Bearer ${idToken}`,
        }
      }
    )
  } catch (emailError) {
    Sentry.addBreadcrumb({
      category: 'auth.signup',
      message: 'welcome_email_failed (best-effort)',
      level: 'warning',
      data: {
        to: email,
        role: userInfo.role,
        errorCode: emailError?.code,
        errorMessage: emailError?.message,
      },
    })
    // Capture for diagnostics; do not block signup UX.
    if (typeof Sentry.captureException === 'function') {
      Sentry.captureException(emailError)
    }
  }
  // Fire internal notifications to leadership based on account type
  try {
    await post({
      subUrl: '/user/new-signup-notification',
      data: {},
      headers: {
        Authorization: `Bearer ${idToken}`,
      }
    });
  } catch (notifyError) {
    // Best-effort: log and continue without blocking signup
    Sentry.addBreadcrumb({
      category: 'auth.signup',
      message: 'new_signup_notification_failed (best-effort)',
      level: 'warning',
      data: {
        errorCode: notifyError?.code,
        errorMessage: notifyError?.message,
      },
    })
    if (typeof Sentry.captureException === 'function') {
      Sentry.captureException(notifyError)
    }
  }
  await sendEmailVerification(createdUser.user);
};

export const updateAvatar = ({ file, uid }) => async (dispatch, getState) => {
  const { auth: { user } } = getState()
  const userRef = doc(firestoreDb, "users", uid)
  const imageRef = ref(storage, `users/${uid}`)
  await uploadBytes(imageRef, file)
  const imageUrl = await getDownloadURL(imageRef)

  // update user info
  await updateDoc(userRef, {
    avatar: imageUrl
  })

  dispatch({
    type: Auth.SET_USER,
    payload: {
      user: {
        ...user,
        avatar: imageUrl,
      },
    },
  });
}

export const resetPassword = ({ email }) => {
  return sendPasswordResetEmail(firebaseAuth, email)
}

export const acceptTerms = () => async (dispatch, getState) => {
  const { auth: { user } } = getState()
  const userRef = doc(firestoreDb, "users", user.uid);

  await updateDoc(userRef, {
    agreedToRules: true
  })

  dispatch({
    type: Auth.SET_USER,
    payload: {
      user: {
        ...user,
        agreedToRules: true,
      },
    },
  });
}
